'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { isDurableAssetId } from '@/lib/expiring-media-url';
import { assetApi } from '@/lib/api/assets';
import { MediaDeliveryCache, clearDisplayedMedia, type DeliverySnapshot } from '@/lib/media-delivery';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';

let sessionVersion = getAuthSessionVersion();
const cache = new MediaDeliveryCache(async (ids, signal) => {
  const response = await assetApi.getDelivery(ids, signal);
  if (!response.success || !response.data) throw new Error('Media delivery unavailable');
  return response.data.items;
});
const emptyServerSnapshot: DeliverySnapshot = Object.freeze({ loading: false });
const pendingServerSnapshot: DeliverySnapshot = Object.freeze({ loading: true });

/** Explicit user retry reauthorizes an asset and renews its delivery URLs. */
export function refreshMediaDelivery(assetId: string) { cache.refresh(assetId); }

export function useMediaDelivery(assetId?: string | null, enabled = true): DeliverySnapshot {
  // Re-subscribe on logout/account switch; never expose another session's signed URLs.
  const userId = useAuthStore(state => state.user?.id);
  const version = getAuthSessionVersion();
  if (sessionVersion !== version) { cache.clear(); clearDisplayedMedia(); sessionVersion = version; }
  const id = enabled && userId && isDurableAssetId(assetId) ? assetId : undefined;
  const subscribe = useCallback((listener: () => void) => id ? cache.subscribe(id, listener) : () => {}, [id, userId, version]);
  const getSnapshot = useCallback(() => cache.get(id), [id, userId, version]);
  // Hydration must not publish an idle asset before its first authorized lookup.
  // Otherwise media consumers start an original/optimizer fetch in that frame.
  const getServerSnapshot = useCallback(() => enabled && isDurableAssetId(assetId)
    ? pendingServerSnapshot : emptyServerSnapshot, [enabled, assetId]);
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
